import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const EN_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Products', path: '/products' },
  { label: 'For Suppliers', path: '/suppliers' },
  { label: 'Contact', path: '/contact' },
];

const FR_LINKS = [
  { label: 'Accueil', path: '/fr' },
  { label: 'Services', path: '/fr/services' },
  { label: 'Produits', path: '/fr/produits' },
  { label: 'DEVENIR FOURNISSEUR', path: '/fr/fournisseurs' },
  { label: 'Contact', path: '/fr/contact' },
];

// Map EN paths to FR equivalents and vice versa
const EN_TO_FR: Record<string, string> = {
  '/': '/fr',
  '/services': '/fr/services',
  '/products': '/fr/produits',
  '/suppliers': '/fr/fournisseurs',
  '/contact': '/fr/contact',
  '/terms': '/fr/conditions',
};

const FR_TO_EN: Record<string, string> = {
  '/fr': '/',
  '/fr/services': '/services',
  '/fr/produits': '/products',
  '/fr/fournisseurs': '/suppliers',
  '/fr/contact': '/contact',
  '/fr/conditions': '/terms',
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isFrench = location.pathname.startsWith('/fr');
  const navLinks = isFrench ? FR_LINKS : EN_LINKS;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLanguageSwitch = (targetLang: 'en' | 'fr') => {
    const path = location.pathname;
    if (targetLang === 'fr' && !isFrench) {
      // Try exact map first, then strip leading slash and prepend /fr/
      const dest = EN_TO_FR[path] ?? (path === '/' ? '/fr' : `/fr${path}`);
      navigate(dest);
    } else if (targetLang === 'en' && isFrench) {
      // Try exact map first, then strip /fr prefix
      const dest = FR_TO_EN[path] ?? (path === '/fr' ? '/' : path.replace(/^\/fr/, ''));
      navigate(dest);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to={isFrench ? '/fr' : '/'} className="flex items-center cursor-pointer">
          <div className="bg-white rounded-lg px-4 py-2">
            <img
              src="https://storage.readdy-site.link/project_files/f6608a11-bffc-43fe-b4fe-e188634774f2/410f07b3-9a07-4d96-ad6c-8cd90865ebc1_maxsourc3mugs.jpg?v=01df200e03a67f06d87190a3955ab7c3"
              alt="Max Sourcing Logo"
              className="h-14 w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-semibold tracking-wide uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === link.path
                  ? 'text-emerald-700'
                  : scrolled
                  ? 'text-gray-700 hover:text-emerald-700'
                  : 'text-white hover:text-emerald-300'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Language switcher */}
        <div className="hidden md:flex items-center gap-4">
          <div className={`flex items-center rounded-full border text-xs font-bold tracking-widest overflow-hidden whitespace-nowrap ${
            scrolled ? 'border-stone-300' : 'border-white/40'
          }`}>
            <button
              onClick={() => handleLanguageSwitch('en')}
              className={`px-3 py-1.5 cursor-pointer transition-colors ${
                !isFrench
                  ? scrolled ? 'bg-emerald-600 text-white' : 'bg-white/20 text-white'
                  : scrolled ? 'text-gray-500 hover:text-emerald-700 hover:bg-stone-100' : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => handleLanguageSwitch('fr')}
              className={`px-3 py-1.5 cursor-pointer transition-colors ${
                isFrench
                  ? scrolled ? 'bg-emerald-600 text-white' : 'bg-white/20 text-white'
                  : scrolled ? 'text-gray-500 hover:text-emerald-700 hover:bg-stone-100' : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              FR
            </button>
          </div>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-0.5 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
          <span className={`block w-6 h-0.5 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
          <span className={`block w-6 h-0.5 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className="text-sm font-semibold uppercase tracking-wide text-gray-700 hover:text-emerald-700 cursor-pointer"
            >
              {link.label}
            </Link>
          ))}
          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Langue / Language:</span>
            <button
              onClick={() => { handleLanguageSwitch('en'); setMobileOpen(false); }}
              className={`text-xs font-bold px-3 py-1 rounded-full cursor-pointer ${!isFrench ? 'bg-emerald-600 text-white' : 'text-gray-500 border border-gray-200 hover:text-emerald-700'}`}
            >
              EN
            </button>
            <button
              onClick={() => { handleLanguageSwitch('fr'); setMobileOpen(false); }}
              className={`text-xs font-bold px-3 py-1 rounded-full cursor-pointer ${isFrench ? 'bg-emerald-600 text-white' : 'text-gray-500 border border-gray-200 hover:text-emerald-700'}`}
            >
              FR
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
